import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { HIRES, LG, MD, SM } from '@utils/imageutils';
import { useTranslation } from 'react-i18next';
import ProductImage from '@stone/components/product/ProductImage';
import ProductVideo from '@stone/components/product/ProductVideo';
import ProductImageButton from '@stone/components/product/ProductImageButton';
import ProductZoomGallery from '@stone/components/product/ProductZoomGallery';
import SwiperCore, { Keyboard, Pagination } from 'swiper';
import { Swiper as SwiperInstance, SwiperSlide } from 'swiper/react';
import ProductSelection from '@stone/components/product/ProductSelection';
import Icon from '@stone/ui/icon/Icon';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import useBreakpoint from '@common/utils/useBreakpoint';
import { getFirstTabbable } from '@common/scripts/utilities/focusableElementUtils';
import { isVideo } from '@common/scripts/utilities/videosUtils';
import { motion } from 'framer-motion';
import { transitionIn } from '../routerTransitions/transitions';
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
import cookie from 'react-cookies';
import classNames from 'classnames';
import { debounce } from '@common/utils/functions';
import customEventBus from '@common/scripts/libs/eventBus';
SwiperCore.use([Pagination, Keyboard]);
const imageSizes = [SM, MD, LG, HIRES];
const markers = cookie.load('gsap_markers') === 'product-prlx';
const ProductHero = (props) => {
    const { product } = props;
    const { t } = useTranslation();
    // @ts-ignore disable-next-line
    const currentBreakpoint = useBreakpoint();
    const heroRef = useRef(null);
    const endRef = useRef(null);
    const ctasRef = useRef(null);
    const stickyColumnRef = useRef(null);
    // * ANCHOR - USE STATE ----------------------------------------------------------------------
    const [showSticky, setShowSticky] = useState(false);
    const [galleryInitialSlide, setGalleryInitialSlide] = useState(-1);
    const [buttons, setButtons] = useState([]);
    const [pinned, setPinned] = useState(false);
    // * ANCHOR - USE MEMO ----------------------------------------------------------------------
    //@ts-ignore disable-next-line
    const isAnchorScrollVisible = useMemo(() => { var _a; return currentBreakpoint >= window.mqObj.lg && props.anchorToScroll && ((_a = props === null || props === void 0 ? void 0 : props.media) === null || _a === void 0 ? void 0 : _a.length) > 4; }, [currentBreakpoint, props.media]);
    const looks = useMemo(() => {
        var _a;
        return (_a = product === null || product === void 0 ? void 0 : product.lookProducts) === null || _a === void 0 ? void 0 : _a.filter((l) => l !== null);
    }, [product]);
    const animVariants = {
        initial: {
            opacity: 0,
            y: 20,
        },
        animate: Object.assign({ opacity: 1, y: 0 }, transitionIn),
    };
    //create a useffect that create a resize observer to stickyColRef and create a css var on the body to set the stickyColRef height
    useEffect(() => {
        if (stickyColumnRef.current) {
            const observer = new ResizeObserver(debounce((entries) => {
                const width = entries[0].contentRect.width;
                document.body.style.setProperty('--sticky-col-width', `${width}px`);
            }, 1000));
            observer.observe(stickyColumnRef.current);
            return () => observer.disconnect();
        }
    }, [stickyColumnRef.current]);
    // ANCHOR - FUNCTIONS --------------------------------------------------------------------
    const scrollToAnimation = (e) => {
        e.preventDefault();
        const productContent = document.getElementById('product-content');
        const stickyBarHeight = document.querySelector('.sticky-tray').getBoundingClientRect().y;
        gsap.to(window, {
            duration: 1,
            ease: 'power1.inOut',
            scrollTo: {
                y: productContent,
                offsetY: window.innerHeight,
            },
            onComplete: () => gsap.to(window, {
                duration: 1,
                ease: 'power1.inOut',
                delay: 0.1,
                scrollTo: {
                    y: productContent,
                    offsetY: -stickyBarHeight,
                },
                onComplete: () => {
                    var _a;
                    (_a = getFirstTabbable(productContent)) === null || _a === void 0 ? void 0 : _a.focus();
                },
            }),
        });
    };
    const handleButtonState = (id, state) => {
        let arr = [];
        const current = buttons.find((el) => el.id === id) || null;
        if (current) {
            arr = buttons.filter((el) => el.id !== id);
        }
        arr.push({ id, state });
        setButtons(arr);
    };
    // ANCHOR - COMPONENTS --------------------------------------------------------------------
    const heroGalleryImage = (mediaUrl, index, alt) => {
        var _a;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "product-image--wrapper" },
                React.createElement(ProductImageButton, { onImageClick: () => {
                        setGalleryInitialSlide(index);
                    } }, isVideo(mediaUrl) ? (React.createElement("div", { className: "product-image" },
                    React.createElement("div", { className: "product-video-wrapper" },
                        React.createElement(ProductVideo, { url: mediaUrl, inGallery: true, overrideButtonID: `button-video-${index}`, handleOverrideButtonState: handleButtonState }),
                        currentBreakpoint < window.mqObj.lg && (React.createElement("button", { className: "product-video-pause", id: `button-video-${index}`, "aria-label": t('button.pause'), "data-pause": true, "aria-pressed": "false" }, ((_a = buttons === null || buttons === void 0 ? void 0 : buttons.find((el) => el.id === `button-video-${index}`)) === null || _a === void 0 ? void 0 : _a.state) === 'PAUSED' ? (React.createElement(React.Fragment, null,
                            React.createElement(Icon, { family: "16", name: "play" }),
                            React.createElement("span", null, t('pdp.button.videoplay')))) : (React.createElement(React.Fragment, null,
                            React.createElement(Icon, { family: "16", name: "pause" }),
                            React.createElement("span", null, t('pdp.button.videopause'))))))))) : (React.createElement(ProductImage, { ariaHidden: false, img: { alt: alt, urls: props.media }, sizes: imageSizes, index: index, productId: index == 0 ? product === null || product === void 0 ? void 0 : product.id : undefined }))))));
    };
    const heroImagesGrid = () => {
        return (React.createElement("ul", { className: "row product-hero-gallery" }, props.media.map((url, index) => {
            var _a;
            const _isVideo = isVideo(url);
            const captionText = t(_isVideo ? 'pdp.a11y.videocaption' : 'pdp.a11y.imagecaption', [product.productName, _isVideo ? index + 1 : '']);
            const key = `figure-image-${index}`;
            return (React.createElement("li", { className: "col-12", key: key },
                React.createElement("figure", { "aria-label": (_isVideo && captionText) || null },
                    heroGalleryImage(url, index, captionText),
                    React.createElement("div", { className: "row product-image-description" },
                        React.createElement("div", { className: "col-lg-6" },
                            React.createElement("figcaption", { "aria-hidden": true, className: "product-image-description__index" }, (index + 1).toString().padStart(3, '0'))),
                        _isVideo && (React.createElement("div", { className: "col-fill d-flex justify-end" },
                            React.createElement("button", { className: "product-video-pause", id: `button-video-${index}`, "aria-label": t('button.pause'), "data-pause": true, "aria-pressed": "false" }, ((_a = buttons === null || buttons === void 0 ? void 0 : buttons.find((el) => el.id === `button-video-${index}`)) === null || _a === void 0 ? void 0 : _a.state) === 'PAUSED' ? (React.createElement(React.Fragment, null,
                                React.createElement(Icon, { family: "16", name: "play" }),
                                React.createElement("span", null, t('pdp.button.videoplay')))) : (React.createElement(React.Fragment, null,
                                React.createElement(Icon, { family: "16", name: "pause" }),
                                React.createElement("span", null, t('pdp.button.videopause')))))))))));
        })));
    };
    useLayoutEffect(() => {
        var _a;
        if (heroRef.current) {
            //@ts-ignore disable-next-line
            if (currentBreakpoint < window.mqObj.lg) {
                (_a = ScrollTrigger.getById('pdp-prlx')) === null || _a === void 0 ? void 0 : _a.kill(true);
                heroRef.current.removeAttribute('style');
            }
            else {
                ScrollTrigger.create({
                    id: 'pdp-prlx',
                    start: () => 'bottom bottom',
                    trigger: heroRef.current,
                    end: 'bottom top-=100%',
                    pinSpacing: false,
                    pin: true,
                    onToggle: (self) => setPinned(self.isActive),
                    markers: markers,
                    invalidateOnRefresh: true,
                });
            }
        }
        return () => { var _a; return (_a = ScrollTrigger.getById('pdp-prlx')) === null || _a === void 0 ? void 0 : _a.kill(true); };
    }, [currentBreakpoint]);
    useEffect(() => {
        //a11y fix remove scroll trigger if keyboard navigation
        let onKeyborardModeEnabled = (data) => {
            var _a, _b, _c;
            if (data.isKeyboardMode) {
                (_a = ScrollTrigger.getById('pdp-prlx')) === null || _a === void 0 ? void 0 : _a.disable();
            }
            else {
                (_b = ScrollTrigger.getById('pdp-prlx')) === null || _b === void 0 ? void 0 : _b.enable();
                (_c = ScrollTrigger.getById('pdp-prlx')) === null || _c === void 0 ? void 0 : _c.refresh();
            }
        };
        customEventBus.on('keyboard:mode', onKeyborardModeEnabled);
        return () => {
            customEventBus.off('keyboard:mode', onKeyborardModeEnabled);
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "product-hero--wrapper" },
            React.createElement("div", { className: "product-hero", ref: heroRef },
                React.createElement("div", { className: "container" },
                    React.createElement("a", { href: "#pdp-product-info", onClick: (e) => {
                            document.getElementById('pdp-product-info').focus();
                        }, className: "button visible-on-focus button--secondary skip-productgallery", "aria-label": t('pdp.skiptoinfo') },
                        React.createElement(Icon, { name: isAnchorScrollVisible ? 'right-chevron' : 'down-double-chevron', family: "10" })),
                    React.createElement("div", { className: "row" },
                        React.createElement("div", { className: "col-lg-14 col-xxl-16 product-hero__col--notsticky", style: { order: 1 } }, props.media.length > 0 && (React.createElement(React.Fragment, null, currentBreakpoint < window.mqObj.lg ? (React.createElement(HeroImagesCarousel, Object.assign({}, props, { heroGalleryImage: heroGalleryImage }))) : (React.createElement(motion.div, { variants: animVariants, initial: "initial", animate: "animate", role: "region", "aria-label": t('pdp.a11y.imagegallery') }, heroImagesGrid()))))),
                        isAnchorScrollVisible && (React.createElement("div", { className: "col-1", style: { order: 2 } },
                            React.createElement("div", { className: classNames('product-hero__anchorscroll', { pinned: pinned }) },
                                React.createElement("a", { href: "#product-content", onClick: (e) => scrollToAnimation(e), "aria-label": t('pdp.skiptobottom') },
                                    React.createElement(Icon, { name: "down-double-chevron", family: "10" }))))),
                        React.createElement("div", { className: `col-lg-8 col-xxl-6 ${!isAnchorScrollVisible ? 'offset-lg-1' : ''} product-hero__col--sticky`, ref: stickyColumnRef, id: "pdp-product-info", role: "group", tabIndex: -1, "aria-label": t('label.productdetails'), style: { order: 3 } },
                            React.createElement("div", { className: classNames('product-selection', { 'with-looks': (looks === null || looks === void 0 ? void 0 : looks.length) > 0 }, { pinned: pinned }) },
                                React.createElement(ProductSelection, { ctasRef: ctasRef, size: props.size, contactformurl: props === null || props === void 0 ? void 0 : props.contactformurl, deliveryurl: props.deliveryurl, ordermgrurl: props.ordermgrurl, aftercareurl: props.aftercareurl, imageLoaded: props.heroImageLoaded, sizeguideOpen: props.sizeguideOpen, setSizeguideOpen: props.setSizeguideOpen, 
                                    //@ts-ignore disable-next-line
                                    showSticky: showSticky, product: props.product, inPDP: true, isPdp: props.isPdp }))))))),
        React.createElement(ProductZoomGallery, { initialSlide: galleryInitialSlide, scrollHeight: window.scrollY, onRequestClose: () => setGalleryInitialSlide(-1), product: props.product, media: props.media }),
        React.createElement("div", { className: "end-ref", ref: endRef })));
};
const HeroImagesCarousel = (props) => {
    const { t } = useTranslation();
    const { product, heroGalleryImage } = props;
    const galleryRef = useRef(null);
    useLayoutEffect(() => {
        if (galleryRef.current) {
            const header = document.getElementsByTagName('header')[0];
            //@ts-ignore disable-next-line
            ScrollTrigger.create({
                id: 'pdp-prlx-gallery',
                start: () => `clamp(top top+=${(header === null || header === void 0 ? void 0 : header.clientHeight) + 12})`,
                trigger: galleryRef.current,
                end: 'top top-=100%',
                pinSpacing: false,
                pin: true,
                markers: !!markers,
                invalidateOnRefresh: true,
            });
        }
        return () => { var _a; return (_a = ScrollTrigger.getById('pdp-prlx-gallery')) === null || _a === void 0 ? void 0 : _a.kill(); };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: galleryRef, id: "product-hero-gallery" },
            React.createElement(SwiperInstance, { className: "product-hero-gallery", speed: 1000, slidesPerView: 1, keyboard: true, pagination: {
                    clickable: true,
                }, direction: "horizontal", touchStartPreventDefault: false, watchOverflow: true, threshold: 10, a11y: { slideRole: '' } }, props.media.map((url, index) => {
                const key = `slide-figure-${index}`;
                return (React.createElement(SwiperSlide, { key: key, tag: "figure", "aria-labelledby": key },
                    React.createElement("div", { className: "product-gallery", key: `${product === null || product === void 0 ? void 0 : product.id}${index}` }, heroGalleryImage(url, index))));
            })))));
};
export default ProductHero;
